import React, { useEffect, useRef, useState } from "react";
import { Config } from "../../config";
import { v4 as uuidv4 } from "uuid";
import { useGetClientSecrets } from "../../custom-hooks/queries";
import { Button } from "@headlessui/react";
import { ChatBubbleLeftIcon } from "@heroicons/react/24/outline";
import { useSearchParams } from "react-router-dom";
import { useUserRole } from "../../utils/helperMethods";
import { useClerk } from "@clerk/clerk-react";

export const ResponseTab = ({
  activeTab,
  clientSecrets,
  route,
  heading,
  desc,
}) => {
  const [iframeReady, setIframeReady] = useState(false);
  const [requiresAuthorization, setRequiresAuthorization] = useState(false);
  const [authCode, setAuthCode] = useState(null);
  const [hasStartedSSO, setHasStartedSSO] = useState(false);
  const hasStartedSSORef = useRef(false);
  const [showIframe, setShowIframe] = useState(
    window.sessionStorage.getItem("sso_state")
  );

  const stateId = uuidv4();
  const iframeRef = useRef(null);
  const [ssoOpen, setSsoOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const responses = searchParams.get("page");
  const [iframeUrl, setIframeUrl] = useState(
    `${Config.apiGateway.HC_URL}/clerk_auth`
  );

  const clerk = useClerk();

  const authorizeUrl = `${Config.apiGateway.CLERK_AUTH_URL}?grant_type=authorization_code&client_id=${clientSecrets?.client_id}&client_secret=${clientSecrets?.client_secret}&scope=email openid profile&redirect_uri=${Config.apiGateway.URL_FE}hypercomply-callback&response_type=code&state=${stateId}`;

  const startSSO = () => {
    if (!hasStartedSSO) {
      setHasStartedSSO(true);
      hasStartedSSORef.current = true;
      window.sessionStorage.setItem("sso_state", true);
      // Append the current session's auth ticket to the authorize URL so
      // the popup uses the same session instead of a stale cookie
      const syncedUrl = clerk.buildUrlWithAuth(authorizeUrl);
      window.open(syncedUrl);
    }
  };

  const handleMessage = (event) => {
    const data = event.data;

    // 1. Handle Hypercomply iframe ready
    if (data?.type === "hc_iframe_ready") {
      setIframeReady(true);

      if (data.requires_authorization) {
        if (hasStartedSSORef.current) {
          // SSO popup is in flight — wait for the auth code
          setRequiresAuthorization(true);
        } else {
          // No SSO in progress — session expired, reset to show "View" button
          window.sessionStorage.removeItem("sso_state");
          setRequiresAuthorization(false);
          setHasStartedSSO(false);
        }
      } else {
        setRequiresAuthorization(false);
      }
    }

    // 2. Handle Clerk OAuth response
    if (data?.type === "clerk_oauth_authorization" && data.authorization_code) {
      setAuthCode(data.authorization_code);
    }
  };

  useEffect(() => {
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  // 3. Send auth code to iframe when both iframe is ready and auth code is present
  useEffect(() => {
    if (iframeReady && authCode) {
      iframeRef.current?.contentWindow?.postMessage(
        {
          type: "clerk_oauth_authorization",
          authorization_code: authCode,
          redirect_uri: `${Config.apiGateway.URL_FE}hypercomply-callback`,
        },
        "*"
      );
    }
  }, [iframeReady, authCode]);

  // 4. Send navigation message when route becomes available and iframe is ready
  useEffect(() => {
    if (iframeReady && route && !requiresAuthorization) {
      iframeRef.current?.contentWindow?.postMessage(
        {
          type: "clerk_oauth_navigation",
          path: route,
        },
        "*"
      );
    }
  }, [route, iframeReady, requiresAuthorization]);

  const user = useUserRole();

  return (
    <div className="mb-28 px-5 py-10">
      <div className="flex flex-col items-center justify-center h-auto text-gray-500">
        {window.sessionStorage.getItem("sso_state") || requiresAuthorization ? (
          <></>
        ) : (
          <div className="flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-md border border-indigo-100 w-full max-w-sm text-center p-6">
              <div className="flex flex-col items-center space-y-4">
                {/* Icon */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-10 h-10 text-indigo-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M7 8h10M7 12h6m-6 4h8m1 4v-4a2 2 0 012-2h3a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2h3a2 2 0 012 2v4"
                  />
                </svg>

                {/* Heading */}
                <h2 className="text-xl font-semibold text-gray-800">
                  {heading}
                </h2>

                {/* Description */}
                <p className="text-sm text-gray-500">
                  Please click this button to view the {desc}.
                </p>

                {/* Button */}
                <button
                  onClick={() => startSSO()}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm px-6 py-2 rounded-md shadow-sm transition"
                >
                  View
                </button>
              </div>{" "}
            </div>
          </div>
        )}

        {window.sessionStorage.getItem("sso_state") && (
          <iframe
            ref={iframeRef}
            src={iframeUrl}
            className="w-full"
            style={{ height: "100vh", border: "none", overflow: "hidden" }}
            title="Hypercomply"
            onLoad={() => {
              console.log("iframe loaded");
            }}
            onError={() => {
              console.log("iframe error");
            }}
          />
        )}
      </div>
    </div>
  );
};
